import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Spin, Modal, message } from 'antd';
import { ScanOutlined, ShopOutlined, UserOutlined, HistoryOutlined, TeamOutlined } from '@ant-design/icons';
import { auth } from '@festpix/services';
import { getVendorBooth, Booth, resignAsVendor } from '../../services/vendorService';
import VendorLayout from '../../components/VendorLayout';
import PromoteVendorModal from '../../components/PromoteVendorModal';
import BoothScheduleBadge from '../../components/BoothScheduleBadge';
import { colors, shadows } from '@festpix/ui';

const { Title, Text } = Typography;

export default function VendorHomeScreen() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [booth, setBooth] = useState<Booth | null>(null);
  const [loading, setLoading] = useState(true);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [resignModalOpen, setResignModalOpen] = useState(false);
  const [resignLoading, setResignLoading] = useState(false);

  useEffect(() => {
    loadBoothInfo();
  }, []);

  const loadBoothInfo = async () => {
    try {
      if (!user) return;
      const boothData = await getVendorBooth(user.uid);
      setBooth(boothData);
    } catch (error) {
      console.error('Error loading booth info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResign = async () => {
    setResignLoading(true);
    try {
      await resignAsVendor();
      message.success('Voce foi rebaixado para participante com sucesso.');
      window.location.href = '/';
    } catch (error: any) {
      message.error(error.message || 'Erro ao se rebaixar');
    } finally {
      setResignLoading(false);
      setResignModalOpen(false);
    }
  };

  if (loading) {
    return (
      <VendorLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout boothName={booth?.name}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              background: `${colors.primary}20`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 16px',
            }}
          >
            <UserOutlined style={{ fontSize: 40, color: colors.primary }} />
          </div>
          <Title level={3} style={{ margin: 0, color: colors.text }}>
            Ola, {user?.displayName || user?.email?.split('@')[0] || 'Vendedor'}!
          </Title>
          {booth && (
            <>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: colors.primary,
                  padding: '6px 16px',
                  borderRadius: 20,
                  marginTop: 12,
                }}
              >
                <ShopOutlined style={{ color: '#fff', fontSize: 14 }} />
                <Text style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                  {booth.name}
                </Text>
              </div>
              <BoothScheduleBadge boothId={booth.id} boothName={booth.name} />
            </>
          )}
        </div>

        {/* Main Action Card */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: shadows.lg,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <ScanOutlined style={{ fontSize: 28, color: colors.primary }} />
            <Title level={4} style={{ margin: 0 }}>Nova Venda</Title>
          </div>
          <Text style={{ color: colors.textSecondary, display: 'block', marginBottom: 24, lineHeight: 1.6 }}>
            Escaneie o QR Code do cliente para iniciar uma nova cobranca
          </Text>
          <Button
            type="primary"
            size="large"
            icon={<ScanOutlined />}
            onClick={() => navigate('/vendor/scan')}
            style={{
              width: '100%',
              height: 56,
              fontSize: 18,
              background: colors.success,
              borderColor: colors.success,
              borderRadius: 12,
            }}
          >
            Escanear QR Code
          </Button>
        </Card>

        {/* Transaction History Card */}
        {booth && (
          <Card
            style={{
              borderRadius: 16,
              boxShadow: shadows.lg,
              marginBottom: 24,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <HistoryOutlined style={{ fontSize: 28, color: colors.info }} />
              <Title level={4} style={{ margin: 0 }}>Histórico de Vendas</Title>
            </div>
            <Text style={{ color: colors.textSecondary, display: 'block', marginBottom: 24, lineHeight: 1.6 }}>
              Visualize todas as transações do seu estande e realize estornos se necessário
            </Text>
            <Button
              type="default"
              size="large"
              icon={<HistoryOutlined />}
              onClick={() => navigate('/vendor/transactions')}
              style={{
                width: '100%',
                height: 56,
                fontSize: 18,
                borderRadius: 12,
              }}
            >
              Ver Histórico
            </Button>
          </Card>
        )}

        {/* Promote to Vendor Card */}
        {booth && (
          <Card
            style={{
              borderRadius: 16,
              boxShadow: shadows.lg,
              marginBottom: 24,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <TeamOutlined style={{ fontSize: 28, color: colors.warning }} />
              <Title level={4} style={{ margin: 0 }}>Adicionar Vendedor</Title>
            </div>
            <Text style={{ color: colors.textSecondary, display: 'block', marginBottom: 24, lineHeight: 1.6 }}>
              Escaneie o QR Code de um visitante para promove-lo a vendedor deste estande
            </Text>
            <Button
              type="default"
              size="large"
              icon={<TeamOutlined />}
              onClick={() => setPromoteModalOpen(true)}
              style={{
                width: '100%',
                height: 56,
                fontSize: 18,
                borderRadius: 12,
                borderColor: colors.warning,
                color: colors.warning,
              }}
            >
              Promover a Vendedor
            </Button>
          </Card>
        )}

        {/* No booth warning */}
        {!booth && (
          <Card
            style={{
              borderRadius: 16,
              background: `${colors.warning}10`,
              border: `1px solid ${colors.warning}40`,
            }}
          >
            <Text style={{ color: colors.warning }}>
              Voce ainda nao esta associado a nenhum estande. Entre em contato com o administrador.
            </Text>
          </Card>
        )}

        {/* Resign as Vendor */}
        {booth && (
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Button
              type="text"
              danger
              size="large"
              onClick={() => setResignModalOpen(true)}
              style={{ fontSize: 14 }}
            >
              Deixar de ser Vendedor
            </Button>
          </div>
        )}
      </div>

      {/* Promote Vendor Modal */}
      {booth && (
        <PromoteVendorModal
          open={promoteModalOpen}
          onClose={() => setPromoteModalOpen(false)}
          boothId={booth.id}
          boothName={booth.name}
        />
      )}

      {/* Resign Confirmation Modal */}
      <Modal
        title="Deixar de ser Vendedor"
        open={resignModalOpen}
        onCancel={() => setResignModalOpen(false)}
        confirmLoading={resignLoading}
        okText="Confirmar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        onOk={handleResign}
      >
        <div style={{ padding: '16px 0' }}>
          <Text style={{ display: 'block', marginBottom: 16, fontSize: 16 }}>
            Tem certeza que deseja deixar de ser vendedor?
          </Text>
          <div
            style={{
              padding: 16,
              background: `${colors.error}10`,
              borderRadius: 12,
              border: `1px solid ${colors.error}40`,
            }}
          >
            <Text style={{ color: colors.error, fontWeight: 500 }}>
              Atencao: Esta acao e irreversivel. Voce perdera o acesso ao painel de vendedor
              e precisara ser promovido novamente por outro vendedor ou administrador.
            </Text>
          </div>
        </div>
      </Modal>
    </VendorLayout>
  );
}
